// Unit 2-9: All remaining AP Statistics lessons

export const unit2To9Lessons = [
  {
    id: 'unit2-lesson1',
    title: 'Scatterplots and Correlation',
    unit: 'Unit 2: Exploring Two-Variable Data',
    standards: ['AP Statistics Unit 2.1', 'AP Statistics Unit 2.2'],
    vocabulary: [
      {
        term: 'Scatterplot',
        definition: 'A graph showing the relationship between two quantitative variables.'
      },
      {
        term: 'Correlation (r)',
        definition: 'A numerical measure of the direction and strength of a linear relationship, ranging from -1 to 1.'
      },
      {
        term: 'Positive Association',
        definition: 'As one variable increases, the other tends to increase.'
      },
      {
        term: 'Negative Association',
        definition: 'As one variable increases, the other tends to decrease.'
      },
      {
        term: 'Outlier (bivariate)',
        definition: 'A point that deviates from the overall pattern of the relationship.'
      }
    ],
    concept: 'Scatterplots display the relationship between two quantitative variables. We describe them using: Direction (positive/negative), Form (linear/nonlinear), Strength (strong/moderate/weak), and Outliers.',
    workedExample: {
      problem: 'A scatterplot shows study hours (x) vs. test scores (y) with r = 0.82. Describe and interpret.',
      steps: [
        'Direction: Positive (r > 0 means as study hours increase, test scores tend to increase)',
        'Form: Linear (correlation implies linear relationship)',
        'Strength: Strong (r = 0.82 is close to 1)',
        'Interpretation: "There is a strong, positive, linear relationship between study hours and test scores. Students who study more hours tend to score higher on tests."',
        'Caution: This does NOT prove studying causes higher scores (correlation ≠ causation)'
      ]
    },
    teacherTip: 'Always describe association using direction, form, and strength. Never say "correlation" without specifying it is linear. Never claim causation from correlation alone!',
    practiceQuestions: [
      {
        id: 'u2l1q1',
        question: 'The correlation coefficient r can range from:',
        options: [
          '0 to 1',
          '-1 to 1',
          '0 to 100',
          'Any value'
        ],
        correctAnswer: 1,
        explanation: 'Correlation ranges from -1 (perfect negative) to +1 (perfect positive), with 0 indicating no linear relationship.'
      },
      {
        id: 'u2l1q2',
        question: 'A correlation of r = -0.85 indicates:',
        options: [
          'Weak positive relationship',
          'Strong positive relationship',
          'Strong negative relationship',
          'No relationship'
        ],
        correctAnswer: 2,
        explanation: 'r = -0.85 is close to -1, indicating a strong negative (inverse) linear relationship.'
      },
      {
        id: 'u2l1q3',
        question: 'Which statement is FALSE about correlation?',
        options: [
          'Correlation measures linear relationships',
          'Correlation implies causation',
          'Correlation is affected by outliers',
          'Correlation has no units'
        ],
        correctAnswer: 1,
        explanation: 'Correlation does NOT imply causation. This is a fundamental principle in statistics.'
      },
      {
        id: 'u2l1q4',
        question: 'A scatterplot shows a curved pattern. The correlation r is:',
        options: [
          'Always zero',
          'Not an appropriate measure',
          'Always positive',
          'Always negative'
        ],
        correctAnswer: 1,
        explanation: 'Correlation measures only linear relationships; it is inappropriate for curved (nonlinear) patterns.'
      },
      {
        id: 'u2l1q5',
        question: 'If all points in a scatterplot lie exactly on a straight line with negative slope:',
        options: [
          'r = 0',
          'r = -1',
          'r = 1',
          'r cannot be determined'
        ],
        correctAnswer: 1,
        explanation: 'Perfect negative linear relationship has r = -1.'
      },
      {
        id: 'u2l1q6',
        question: 'Which value of r indicates the weakest linear relationship?',
        options: [
          'r = -0.90',
          'r = -0.10',
          'r = 0.75',
          'r = 0.50'
        ],
        correctAnswer: 1,
        explanation: 'r = -0.10 is closest to zero, indicating the weakest linear relationship among these options.'
      },
      {
        id: 'u2l1q7',
        question: 'An outlier in a scatterplot can:',
        options: [
          'Not affect correlation',
          'Substantially change the correlation',
          'Only affect the mean',
          'Make r negative'
        ],
        correctAnswer: 1,
        explanation: 'Outliers can have a large effect on correlation, especially in small datasets.'
      },
      {
        id: 'u2l1q8',
        question: 'Two variables have r = 0. This means:',
        options: [
          'There is no relationship whatsoever',
          'There is no linear relationship',
          'The variables are independent',
          'All points are the same'
        ],
        correctAnswer: 1,
        explanation: 'r = 0 means no linear relationship, but a nonlinear relationship could still exist.'
      },
      {
        id: 'u2l1q9',
        question: 'Correlation is resistant to:',
        options: [
          'Outliers',
          'Changes in units',
          'Nonlinear patterns',
          'Small sample sizes'
        ],
        correctAnswer: 1,
        explanation: 'Correlation is unitless and does not change when units change (e.g., inches to cm).'
      },
      {
        id: 'u2l1q10',
        question: 'In a scatterplot, the explanatory variable is typically on:',
        options: [
          'The y-axis',
          'The x-axis',
          'Either axis',
          'Neither axis'
        ],
        correctAnswer: 1,
        explanation: 'By convention, the explanatory (independent) variable goes on the x-axis.'
      },
      {
        id: 'u2l1q11',
        question: 'A lurking variable is:',
        options: [
          'The same as the response variable',
          'Not measured but affects both variables',
          'Always categorical',
          'Visible in the scatterplot'
        ],
        correctAnswer: 1,
        explanation: 'A lurking variable is not included in the analysis but influences both variables, potentially creating spurious correlation.'
      },
      {
        id: 'u2l1q12',
        question: 'Which correlation indicates the strongest relationship?',
        options: [
          'r = 0.65',
          'r = -0.92',
          'r = 0.40',
          'r = -0.50'
        ],
        correctAnswer: 1,
        explanation: 'Strength is determined by distance from 0; |-0.92| = 0.92 is closest to 1.'
      },
      {
        id: 'u2l1q13',
        question: 'Correlation between height (inches) and height (feet) is:',
        options: [
          'r = 0',
          'r = 12',
          'r = 1',
          'r = 0.50'
        ],
        correctAnswer: 2,
        explanation: 'Perfect positive linear relationship (they are the same variable in different units), so r = 1.'
      },
      {
        id: 'u2l1q14',
        question: 'A scatterplot shows no clear pattern. The best description is:',
        options: [
          'Strong positive relationship',
          'No apparent relationship',
          'Negative relationship',
          'Curved relationship'
        ],
        correctAnswer: 1,
        explanation: 'No clear pattern indicates no apparent relationship between the variables.'
      },
      {
        id: 'u2l1q15',
        question: 'Switching x and y variables in a scatterplot:',
        options: [
          'Changes the sign of r',
          'Changes the value of r',
          'Does not change r',
          'Makes r = 0'
        ],
        correctAnswer: 2,
        explanation: 'Correlation is symmetric; switching variables does not change its value.'
      },
      {
        id: 'u2l1q16',
        question: 'A football-shaped cluster in a scatterplot suggests:',
        options: [
          'Perfect correlation',
          'Bivariate normal distribution',
          'No relationship',
          'Outliers present'
        ],
        correctAnswer: 1,
        explanation: 'An elliptical (football-shaped) cluster suggests a bivariate normal distribution with linear correlation.'
      },
      {
        id: 'u2l1q17',
        question: 'Correlation measures:',
        options: [
          'Any type of relationship',
          'Only linear relationships',
          'Only causal relationships',
          'Only positive relationships'
        ],
        correctAnswer: 1,
        explanation: 'Correlation specifically measures the strength and direction of linear relationships only.'
      },
      {
        id: 'u2l1q18',
        question: 'A study finds r = 0.90 between ice cream sales and drowning deaths. This likely indicates:',
        options: [
          'Ice cream causes drowning',
          'A lurking variable (e.g., temperature)',
          'A weak relationship',
          'Measurement error'
        ],
        correctAnswer: 1,
        explanation: 'This is a classic example of spurious correlation due to a lurking variable (hot weather increases both).'
      },
      {
        id: 'u2l1q19',
        question: 'Which scenario would likely show a negative correlation?',
        options: [
          'Hours studied vs. test scores',
          'Height vs. weight',
          'Price vs. sales volume',
          'Age vs. income'
        ],
        correctAnswer: 2,
        explanation: 'As price increases, sales volume typically decreases (negative relationship).'
      },
      {
        id: 'u2l1q20',
        question: 'When describing a scatterplot, you should mention:',
        options: [
          'Direction, form, strength',
          'Outliers and unusual features',
          'Context of the variables',
          'All of the above'
        ],
        correctAnswer: 3,
        explanation: 'Complete description includes direction, form, strength, outliers, and context.'
      },
      {
        id: 'u2l1q21',
        question: 'An influential point is one that:',
        options: [
          'Is always an outlier',
          'Strongly affects the correlation or regression line',
          'Has the highest y-value',
          'Is in the center of the data'
        ],
        correctAnswer: 1,
        explanation: 'An influential point has a large effect on statistics like correlation or the least-squares regression line.'
      },
      {
        id: 'u2l1q22',
        question: 'Correlation is NOT resistant because:',
        options: [
          'It uses the mean',
          'It can be heavily influenced by outliers',
          'It requires large samples',
          'It only works for linear data'
        ],
        correctAnswer: 1,
        explanation: 'Like the mean, correlation can be heavily influenced by outliers or extreme points.'
      },
      {
        id: 'u2l1q23',
        question: 'If r = 0.60 for (x, y) data, then r for (y, x) is:',
        options: [
          '-0.60',
          '0.60',
          '0.40',
          '1.00'
        ],
        correctAnswer: 1,
        explanation: 'Correlation is symmetric; the value does not change if you swap x and y.'
      },
      {
        id: 'u2l1q24',
        question: 'A strong correlation between two variables means:',
        options: [
          'One causes the other',
          'They move together predictably',
          'They are independent',
          'They have the same mean'
        ],
        correctAnswer: 1,
        explanation: 'Strong correlation means the variables move together in a predictable linear pattern, but does not imply causation.'
      },
      {
        id: 'u2l1q25',
        question: 'The association between variables is:',
        options: [
          'Always causal',
          'The pattern of relationship seen in data',
          'Only measured by correlation',
          'Always linear'
        ],
        correctAnswer: 1,
        explanation: 'Association is the general pattern of relationship; it can be linear or nonlinear, and does not imply causation.'
      },
      {
        id: 'u2l1q26',
        question: 'Extrapolation means:',
        options: [
          'Calculating correlation',
          'Making predictions beyond the range of data',
          'Finding outliers',
          'Switching variables'
        ],
        correctAnswer: 1,
        explanation: 'Extrapolation is predicting beyond the observed data range, which can be unreliable.'
      },
      {
        id: 'u2l1q27',
        question: 'A nonlinear relationship will typically have:',
        options: [
          'r close to ±1',
          'r close to 0',
          'r that does not accurately reflect the relationship strength',
          'No correlation value'
        ],
        correctAnswer: 2,
        explanation: 'Since r only measures linear relationships, it can be misleading for nonlinear patterns.'
      },
      {
        id: 'u2l1q28',
        question: 'Two variables with a correlation of r = -0.70 have:',
        options: [
          'No relationship',
          'A fairly strong negative linear relationship',
          'A weak positive relationship',
          'A perfect relationship'
        ],
        correctAnswer: 1,
        explanation: 'r = -0.70 indicates a fairly strong negative (inverse) linear relationship.'
      },
      {
        id: 'u2l1q29',
        question: 'Aggregating data (using averages instead of individuals) typically:',
        options: [
          'Decreases correlation',
          'Increases correlation',
          'Does not affect correlation',
          'Makes correlation negative'
        ],
        correctAnswer: 1,
        explanation: 'Aggregation tends to increase correlation by reducing variability (Simpson\'s paradox related).'
      },
      {
        id: 'u2l1q30',
        question: 'The primary purpose of a scatterplot is to:',
        options: [
          'Calculate the mean',
          'Visualize the relationship between two quantitative variables',
          'Show categorical data',
          'Find the median'
        ],
        correctAnswer: 1,
        explanation: 'Scatterplots are specifically designed to display and explore relationships between two quantitative variables.'
      }
    ]
  },
  {
    id: 'unit3-lesson1',
    title: 'Sampling Methods and Bias',
    unit: 'Unit 3: Collecting Data',
    standards: ['AP Statistics Unit 3.1', 'AP Statistics Unit 3.2'],
    vocabulary: [
      {
        term: 'Population',
        definition: 'The entire group of individuals about which we want information.'
      },
      {
        term: 'Sample',
        definition: 'A subset of the population from which we collect data.'
      },
      {
        term: 'Simple Random Sample (SRS)',
        definition: 'A sample where every possible sample of size n has an equal chance of being selected.'
      },
      {
        term: 'Bias',
        definition: 'Systematic favoritism that leads to estimates consistently off target in one direction.'
      },
      {
        term: 'Sampling Variability',
        definition: 'The fact that different samples from the same population give different results.'
      }
    ],
    concept: 'Good sampling methods minimize bias and rely on randomization. A Simple Random Sample (SRS) ensures every individual has an equal chance of selection and is the gold standard for unbiased sampling.',
    workedExample: {
      problem: 'A principal wants to survey students about lunch quality. Method A: Ask the first 50 students who enter the cafeteria. Method B: Randomly select 50 student ID numbers. Which is better and why?',
      steps: [
        'Method A (convenience sampling): Biased because early students may differ systematically (e.g., more motivated, different schedules)',
        'Method B (SRS): Unbiased because every student has an equal chance of selection',
        'Better Method: B, because it eliminates selection bias',
        'Conclusion: "Method B is preferred because it uses random selection, ensuring every student has an equal chance of being surveyed. Method A may overrepresent students with earlier lunch periods or other characteristics."'
      ]
    },
    teacherTip: 'On the AP exam, always identify the type of sample and explain potential sources of bias. Remember: Convenience samples, voluntary response, and non-random methods all introduce bias!',
    practiceQuestions: [
      {
        id: 'u3l1q1',
        question: 'A Simple Random Sample (SRS) is one in which:',
        options: [
          'Every individual is selected',
          'Every possible sample of size n has an equal chance',
          'The first n individuals are chosen',
          'Only volunteers participate'
        ],
        correctAnswer: 1,
        explanation: 'In an SRS, every combination of n individuals from the population has an equal probability of selection.'
      },
      {
        id: 'u3l1q2',
        question: 'Sampling bias occurs when:',
        options: [
          'Samples give different results',
          'The sampling method systematically favors certain outcomes',
          'The sample is small',
          'Random sampling is used'
        ],
        correctAnswer: 1,
        explanation: 'Bias is systematic error that consistently pushes results in one direction, away from the truth.'
      },
      {
        id: 'u3l1q3',
        question: 'A convenience sample is one where:',
        options: [
          'Individuals are randomly selected',
          'Individuals are chosen because they are easy to reach',
          'Every individual has equal chance',
          'Stratified methods are used'
        ],
        correctAnswer: 1,
        explanation: 'Convenience sampling selects individuals based on accessibility, often leading to bias.'
      },
      {
        id: 'u3l1q4',
        question: 'Voluntary response samples typically suffer from:',
        options: [
          'No bias',
          'Undercoverage bias',
          'Strong opinion bias',
          'Random error only'
        ],
        correctAnswer: 2,
        explanation: 'People who volunteer to respond often have strong opinions, making the sample unrepresentative.'
      },
      {
        id: 'u3l1q5',
        question: 'Undercoverage bias occurs when:',
        options: [
          'Some groups in the population are left out',
          'Too many people are surveyed',
          'Random sampling is used',
          'The sample is too large'
        ],
        correctAnswer: 0,
        explanation: 'Undercoverage means some population members have no chance of being selected, biasing results.'
      },
      {
        id: 'u3l1q6',
        question: 'A stratified random sample:',
        options: [
          'Selects individuals from distinct groups (strata)',
          'Is always biased',
          'Does not use randomization',
          'Is the same as a convenience sample'
        ],
        correctAnswer: 0,
        explanation: 'Stratified sampling divides the population into groups and randomly samples from each group.'
      },
      {
        id: 'u3l1q7',
        question: 'Response bias occurs when:',
        options: [
          'The sample is too small',
          'Questions are worded poorly or respondents lie',
          'Random sampling is used',
          'All individuals respond'
        ],
        correctAnswer: 1,
        explanation: 'Response bias happens when the question wording, interviewer presence, or social desirability affects answers.'
      },
      {
        id: 'u3l1q8',
        question: 'Nonresponse bias occurs when:',
        options: [
          'Everyone responds',
          'Selected individuals do not respond and differ from those who do',
          'The sample is random',
          'Questions are clear'
        ],
        correctAnswer: 1,
        explanation: 'Nonresponse bias arises when those who don\'t respond differ systematically from those who do.'
      },
      {
        id: 'u3l1q9',
        question: 'A census means:',
        options: [
          'A small random sample',
          'Data collected from the entire population',
          'Voluntary responses',
          'A biased sample'
        ],
        correctAnswer: 1,
        explanation: 'A census attempts to collect data from every member of the population.'
      },
      {
        id: 'u3l1q10',
        question: 'The main advantage of randomization in sampling is:',
        options: [
          'It guarantees a perfect sample',
          'It eliminates bias',
          'It makes calculations easier',
          'It increases sample size'
        ],
        correctAnswer: 1,
        explanation: 'Randomization eliminates systematic bias, though random variability remains.'
      },
      {
        id: 'u3l1q11',
        question: 'Cluster sampling involves:',
        options: [
          'Randomly selecting groups, then surveying all individuals in selected groups',
          'Selecting individuals one at a time',
          'Stratifying by age',
          'Using convenience'
        ],
        correctAnswer: 0,
        explanation: 'Cluster sampling randomly selects clusters (groups) and includes all members of selected clusters.'
      },
      {
        id: 'u3l1q12',
        question: 'Systematic random sampling selects:',
        options: [
          'Every kth individual from a list',
          'Only volunteers',
          'The first n individuals',
          'Individuals randomly with replacement'
        ],
        correctAnswer: 0,
        explanation: 'Systematic sampling selects every kth individual (e.g., every 10th person on a list) after a random start.'
      },
      {
        id: 'u3l1q13',
        question: 'Which sampling method is most likely to be biased?',
        options: [
          'Simple random sample',
          'Stratified random sample',
          'Voluntary response sample',
          'Cluster sample'
        ],
        correctAnswer: 2,
        explanation: 'Voluntary response samples are highly biased because participants self-select, often with strong opinions.'
      },
      {
        id: 'u3l1q14',
        question: 'The sampling frame is:',
        options: [
          'The population of interest',
          'The list from which the sample is drawn',
          'The sample itself',
          'The survey questions'
        ],
        correctAnswer: 1,
        explanation: 'The sampling frame is the actual list of individuals from which the sample is selected.'
      },
      {
        id: 'u3l1q15',
        question: 'A larger random sample generally:',
        options: [
          'Increases bias',
          'Decreases variability',
          'Guarantees no error',
          'Is always better regardless of cost'
        ],
        correctAnswer: 1,
        explanation: 'Larger samples reduce sampling variability (randomness) but do not eliminate bias from poor methods.'
      },
      {
        id: 'u3l1q16',
        question: 'Which is an example of undercoverage?',
        options: [
          'A phone survey that excludes people without phones',
          'A voluntary online poll',
          'A simple random sample',
          'A census'
        ],
        correctAnswer: 0,
        explanation: 'Excluding people without phones means part of the population has no chance of selection (undercoverage).'
      },
      {
        id: 'u3l1q17',
        question: 'Wording a question as "Don\'t you agree that..." introduces:',
        options: [
          'Nonresponse bias',
          'Response bias',
          'Undercoverage',
          'No bias'
        ],
        correctAnswer: 1,
        explanation: 'Leading questions (suggesting a "correct" answer) introduce response bias.'
      },
      {
        id: 'u3l1q18',
        question: 'A multistage sample:',
        options: [
          'Uses multiple sampling methods in sequence',
          'Is always biased',
          'Cannot be random',
          'Is the same as a simple random sample'
        ],
        correctAnswer: 0,
        explanation: 'Multistage sampling combines methods (e.g., cluster sampling followed by random sampling within clusters).'
      },
      {
        id: 'u3l1q19',
        question: 'The main purpose of random sampling is to:',
        options: [
          'Make data collection easier',
          'Produce an unbiased representation of the population',
          'Increase the sample size',
          'Guarantee correct results'
        ],
        correctAnswer: 1,
        explanation: 'Random sampling aims to produce a sample that is representative and unbiased, though variability remains.'
      },
      {
        id: 'u3l1q20',
        question: 'A sample of 100 from a population of 10,000 and a sample of 100 from a population of 100 million have:',
        options: [
          'The same variability approximately',
          'Very different variability',
          'No relationship',
          'Cannot be compared'
        ],
        correctAnswer: 0,
        explanation: 'Sample variability depends primarily on sample size, not population size (for large populations).'
      },
      {
        id: 'u3l1q21',
        question: 'Sampling error refers to:',
        options: [
          'Mistakes in data collection',
          'Natural variability from sample to sample',
          'Bias in the method',
          'Incorrect calculations'
        ],
        correctAnswer: 1,
        explanation: 'Sampling error is the natural variation due to randomness; it is NOT the same as bias or mistakes.'
      },
      {
        id: 'u3l1q22',
        question: 'Which reduces bias in a survey?',
        options: [
          'Increasing sample size',
          'Using random selection',
          'Asking leading questions',
          'Accepting only volunteers'
        ],
        correctAnswer: 1,
        explanation: 'Random selection eliminates systematic bias; larger samples reduce variability but not bias.'
      },
      {
        id: 'u3l1q23',
        question: 'A quota sample:',
        options: [
          'Randomly selects individuals',
          'Fills quotas for subgroups but uses non-random selection',
          'Is always unbiased',
          'Is the same as stratified random sampling'
        ],
        correctAnswer: 1,
        explanation: 'Quota sampling ensures group representation but often uses convenience, introducing bias.'
      },
      {
        id: 'u3l1q24',
        question: 'A parameter is:',
        options: [
          'A numerical summary of a sample',
          'A numerical summary of a population',
          'Always unknown',
          'The same as a statistic'
        ],
        correctAnswer: 1,
        explanation: 'A parameter describes the population (e.g., population mean μ), while a statistic describes a sample.'
      },
      {
        id: 'u3l1q25',
        question: 'A statistic is:',
        options: [
          'A numerical summary of a population',
          'A numerical summary of a sample',
          'Always biased',
          'Only the mean'
        ],
        correctAnswer: 1,
        explanation: 'A statistic is calculated from sample data (e.g., sample mean x̄).'
      },
      {
        id: 'u3l1q26',
        question: 'Random digit dialing for phone surveys can suffer from:',
        options: [
          'No bias',
          'Undercoverage (cell-only households)',
          'Perfect representation',
          'Too large a sample'
        ],
        correctAnswer: 1,
        explanation: 'Traditional random digit dialing may undercover cell-only users or non-phone households.'
      },
      {
        id: 'u3l1q27',
        question: 'The Literary Digest poll of 1936 failed because of:',
        options: [
          'Too large a sample',
          'Undercoverage and nonresponse bias',
          'Perfect random sampling',
          'Asking too many questions'
        ],
        correctAnswer: 1,
        explanation: 'The poll sampled from phone books and car registrations (undercoverage) and had low response rate (nonresponse).'
      },
      {
        id: 'u3l1q28',
        question: 'A sampling method is unbiased if:',
        options: [
          'It gives the correct answer every time',
          'It has no systematic tendency to over- or underestimate',
          'The sample is large',
          'Everyone responds'
        ],
        correctAnswer: 1,
        explanation: 'An unbiased method produces estimates that are correct on average, with no systematic error.'
      },
      {
        id: 'u3l1q29',
        question: 'Sampling with replacement means:',
        options: [
          'Each selected individual can be selected again',
          'Individuals are returned to the population',
          'The sample is biased',
          'The sample is smaller'
        ],
        correctAnswer: 0,
        explanation: 'With replacement, each selection is independent; an individual can appear multiple times in the sample.'
      },
      {
        id: 'u3l1q30',
        question: 'Which is the gold standard for unbiased sampling?',
        options: [
          'Convenience sample',
          'Simple Random Sample (SRS)',
          'Voluntary response',
          'Quota sample'
        ],
        correctAnswer: 1,
        explanation: 'Simple Random Sampling ensures every individual and every possible sample has equal selection probability.'
      }
    ]
  }
];